/**
 * Piezas del ingreso: el botón que lleva a Cognito, la vuelta con el código y la puerta de las páginas
 * con sesión. La página de ingreso la compone `pages`; aquí no hay maquetación.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { t } from "@/shared/i18n";
import { Aviso, Boton, Cargando } from "@/shared/ui";
import { ErrorIngreso, type Retorno } from "../api/oauth";
import { tienePermiso } from "../model/jwt";
import { useSesion } from "../model/sesion";

const s = t.sesion;

/** Sin sesión muestra `sinSesion`; con sesión pero sin el permiso, `sinPermiso`. */
export function RequiereSesion({
  sinSesion,
  sinPermiso,
  children,
}: {
  readonly sinSesion: ReactNode;
  readonly sinPermiso: ReactNode;
  readonly children: ReactNode;
}) {
  const { empleado } = useSesion();
  if (!empleado) return sinSesion;
  if (!tienePermiso(empleado)) return sinPermiso;
  return children;
}

/** Lleva a la página de ingreso de Cognito; al volver, se abre la ruta que se había pedido. */
export function BotonIngresar() {
  const { identidad } = useSesion();
  const { pathname, search } = useLocation();
  const [yendo, setYendo] = useState(false);
  return (
    <Boton
      variante="principal"
      disabled={yendo}
      onClick={() => {
        setYendo(true);
        void identidad.iniciar(`${pathname}${search}`).catch(() => {
          setYendo(false);
        });
      }}
    >
      {yendo ? s.redirigiendo : s.ingresar}
    </Boton>
  );
}

export function AvisoSesionTerminada() {
  const { terminada } = useSesion();
  if (!terminada) return null;
  return (
    <Aviso rol="status">
      <b>{s.terminoTitulo}.</b> {s.terminoTexto}
    </Aviso>
  );
}

/**
 * Un código de Cognito se canjea una sola vez. React, en desarrollo, monta dos veces: la promesa se
 * guarda por código para no canjearlo de nuevo.
 */
const enCurso = new Map<string, Promise<Retorno>>();

/** Página de vuelta (`/ingreso`): canjea el código y abre la ruta pedida. */
export function CompletarIngreso({ accionError }: { readonly accionError: ReactNode }) {
  const { identidad, iniciar } = useSesion();
  const { search } = useLocation();
  const navegar = useNavigate();
  const [error, setError] = useState<ErrorIngreso["motivo"] | null>(null);
  const hecho = useRef(false);

  useEffect(() => {
    if (hecho.current) return;
    hecho.current = true;
    const clave = search || "vacio";
    let p = enCurso.get(clave);
    if (!p) {
      p = identidad.completar(search);
      enCurso.set(clave, p);
    }
    p.then(
      (r) => {
        iniciar(r.tokens);
        void navegar(r.volver, { replace: true });
      },
      (e: unknown) => {
        setError(e instanceof ErrorIngreso ? e.motivo : "canje");
      },
    ).finally(() => {
      // Dejarla un momento permite que el segundo montaje de desarrollo la reutilice.
      setTimeout(() => enCurso.delete(clave), 1000);
    });
  }, [identidad, iniciar, navegar, search]);

  if (!error) return <Cargando texto={s.completando} />;
  return (
    <div role="alert">
      <Aviso tono="grave">
        <b>{s.errorRetornoTitulo}.</b> {error === "cancelado" ? s.cancelado : s.errorRetornoTexto}
      </Aviso>
      {accionError}
    </div>
  );
}

export function BotonSalir({ variante = "secundario" }: { readonly variante?: "secundario" | "principal" }) {
  const { salir } = useSesion();
  return (
    <Boton variante={variante} onClick={() => void salir()}>
      {t.navegacion.salir}
    </Boton>
  );
}
